/**
 * EANCOM/EDIFACT D96A Parser
 * Based on EANCOM (DEDIP D96) documentation
 */

// Default delimiters for EDIFACT
const DEFAULT_DELIMITERS = {
  segmentTerminator: "'",
  componentDataSeparator: ":",
  dataElementSeparator: "+",
  releaseCharacter: "?",
  repetitionSeparator: "*"
};

/**
 * Main parser function for EANCOM/EDIFACT D96A files
 * @param {string} ediContent - Raw content of the EDI file
 * @param {function} progressCallback - Optional callback for progress updates
 * @returns {object} Structured data extracted from the EDI content
 */
const eancomParser = (ediContent, progressCallback = null) => {
  if (!ediContent) {
    throw new Error('No EDI content provided');
  }

  const updateProgress = (stage, progress) => {
    if (progressCallback) {
      progressCallback(stage, progress);
    }
  };

  updateProgress('delimiters', 5);
  // Detect and set delimiters (usually defined in UNA segment)
  const delimiters = detectDelimiters(ediContent);
  
  updateProgress('segments', 15);
  // Parse into segments (optimized for large files)
  const segments = parseSegmentsOptimized(ediContent, delimiters, updateProgress);
  
  updateProgress('interchange', 35);
  // Extract interchange information
  const interchangeHeader = extractInterchangeInfo(segments);
  
  updateProgress('messages', 45);
  // Check for multiple messages and create message slices
  const messageSlices = createMessageSlices(segments, delimiters, interchangeHeader);
  
  updateProgress('structure', 60);
  // Extract message information
  const messageInfo = extractMessageInfo(segments);
  
  updateProgress('dates', 70);
  // Extract dates
  const dates = extractDates(segments);
  
  updateProgress('counts', 80);
  // Build segment counts
  const segmentCounts = countSegmentTypes(segments);
  
  updateProgress('parsing', 85);
  // Parse document structure
  const structuredSegments = parseStructure(segments);

  updateProgress('totals', 90);
  // Extract line counts and totals
  const documentTotals = extractDocumentTotals(segments);
  
  updateProgress('lineItems', 95);
  // Extract line items with PIA information
  const lineItems = extractLineItems(segments);
  
  updateProgress('complete', 100);
  
  return {
    interchangeHeader,
    segments: structuredSegments,
    messageType: messageInfo.type,
    version: messageInfo.version,
    messageReference: messageInfo.reference,
    sender: messageInfo.sender,
    recipient: messageInfo.recipient,
    parties: messageInfo.parties,
    delimiters,
    segmentCounts,
    dates,
    documentTotals,
    lineItems,
    // Add message slices for multi-document support
    messages: messageSlices
  };
};

/**
 * Build a parsed data object from a provided segment array (e.g., a UNH..UNT slice)
 * Optionally provide base data for fields unavailable inside the slice (like interchange header or delimiters)
 */
const buildParsedFromSegments = (segments, base = {}) => {
  if (!Array.isArray(segments) || segments.length === 0) {
    return {
      interchangeHeader: base.interchangeHeader || { sender: 'Unknown', recipient: 'Unknown', date: 'Unknown', controlRef: 'Unknown' },
      segments: [],
      messageType: 'Unknown',
      version: 'Unknown',
      messageReference: 'Unknown',
      sender: { id: 'Unknown', name: 'Unknown' },
      recipient: { id: 'Unknown', name: 'Unknown' },
      parties: {},
      delimiters: base.delimiters || DEFAULT_DELIMITERS,
      segmentCounts: {},
      dates: {},
      documentTotals: {},
      lineItems: []
    };
  }

  const messageInfo = extractMessageInfo(segments);
  const structuredSegments = parseStructure(segments);
  const segmentCounts = countSegmentTypes(segments);
  const dates = extractDates(segments);
  const documentTotals = extractDocumentTotals(segments);
  const lineItems = extractLineItems(segments);

  return {
    interchangeHeader: base.interchangeHeader || extractInterchangeInfo(segments),
    segments: structuredSegments,
    messageType: messageInfo.type,
    version: messageInfo.version,
    messageReference: messageInfo.reference,
    sender: messageInfo.sender,
    recipient: messageInfo.recipient,
    parties: messageInfo.parties,
    delimiters: base.delimiters || DEFAULT_DELIMITERS,
    segmentCounts,
    dates,
    documentTotals,
    lineItems
  };
};

/**
 * Detects the delimiters used in the EDI document
 * @param {string} content - Raw EDI content
 * @returns {object} - The delimiter configuration
 */
const detectDelimiters = (content) => {
  // Look for UNA segment which defines the delimiters
  if (content.startsWith('UNA')) {
    return {
      componentDataSeparator: content.charAt(3),
      dataElementSeparator: content.charAt(4),
      decimalNotation: content.charAt(5),
      releaseCharacter: content.charAt(6),
      repetitionSeparator: content.charAt(7),
      segmentTerminator: content.charAt(8)
    };
  }
  
  // If no UNA segment, use default delimiters
  return DEFAULT_DELIMITERS;
};

/**
 * Optimized segment parsing for large files with progress updates
 * @param {string} content - Raw EDI content
 * @param {object} delimiters - The delimiters used in the document
 * @param {function} progressCallback - Progress callback function
 * @returns {array} Array of segment objects
 */
const parseSegmentsOptimized = (content, delimiters, progressCallback = null) => {
  // Replace release characters before splitting
  let processedContent = content;
  if (delimiters.releaseCharacter) {
    processedContent = processReleaseCharacters(content, delimiters.releaseCharacter);
  }
  
  // Split into segments by segment terminator
  const segmentStrings = processedContent.split(delimiters.segmentTerminator)
    .map(s => s.trim())
    .filter(s => s.length > 0);
  
  const segments = [];
  const totalSegments = segmentStrings.length;
  const chunkSize = Math.max(50, Math.floor(totalSegments / 10)); // Process in chunks
  
  // Process segments in chunks to avoid blocking
  for (let i = 0; i < segmentStrings.length; i += chunkSize) {
    const chunk = segmentStrings.slice(i, i + chunkSize);
    
    // Parse each segment in the chunk
    const parsedChunk = chunk.map(segmentString => parseSegment(segmentString, delimiters));
    segments.push(...parsedChunk);
    
    // Update progress
    if (progressCallback) {
      const progress = 15 + ((i + chunkSize) / totalSegments) * 20; // 15-35% range
      progressCallback('segments', Math.min(progress, 35));
    }
  }
  
  return segments;
};

/**
 * Parse a single segment
 * @param {string} segmentString - Raw segment string
 * @param {object} delimiters - The delimiters used in the document
 * @returns {object} Parsed segment object
 */
const parseSegment = (segmentString, delimiters) => {
  // Skip UNA segment as it's already processed
  if (segmentString.startsWith('UNA')) {
    return {
      id: 'UNA',
      elements: []
    };
  }
  
  // Parse data elements
  const elements = segmentString.split(delimiters.dataElementSeparator);
  const segmentId = elements.shift(); // First element is segment ID
  
  // Parse component data elements
  const parsedElements = elements.map(element => {
    if (element.includes(delimiters.componentDataSeparator)) {
      // Process component data elements
      const components = element.split(delimiters.componentDataSeparator);
      
      // Special handling for segments with known component structures
      if (segmentId === 'DTM' && components.length >= 2) {
        // Handle DTM segment components properly
        // Format: DTM+137:20220415:102' -> [137, 20220415, 102]
        return components;
      } else if (['NAD', 'CTA', 'RFF', 'MOA', 'CUX'].includes(segmentId)) {
        // These segments also have well-defined composite structures
        return components;
      } else {
        // Default handling for other composites
        return components;
      }
    }
    return element;
  });
  
  return {
    id: segmentId,
    elements: parsedElements
  };
};

/**
 * Create message slices for multi-document files
 * @param {array} segments - All parsed segments
 * @param {object} delimiters - The delimiters used
 * @param {object} interchangeHeader - Interchange header info
 * @returns {array} Array of message slice objects
 */
const createMessageSlices = (segments, delimiters, interchangeHeader) => {
  const messageSlices = [];
  const unhIndices = [];
  const untIndices = [];
  
  // Find all UNH and UNT segment indices
  segments.forEach((segment, index) => {
    if (segment.id === 'UNH') {
      unhIndices.push(index);
    } else if (segment.id === 'UNT') {
      untIndices.push(index);
    }
  });
  
  // Create slices for each UNH...UNT pair
  if (unhIndices.length > 0 && unhIndices.length === untIndices.length) {
    for (let i = 0; i < unhIndices.length; i++) {
      const startIndex = unhIndices[i];
      const endIndex = untIndices[i];
      
      if (startIndex < endIndex) {
        const messageSegments = segments.slice(startIndex, endIndex + 1);
        const parsed = buildParsedFromSegments(messageSegments, {
          delimiters,
          interchangeHeader
        });
        
        const unhSegment = segments[startIndex];
        const messageRef = unhSegment.elements?.[0] || `msg-${i + 1}`;
        const messageType = unhSegment.elements?.[1]?.[0] || 'Unknown';
        
        messageSlices.push({
          meta: {
            index: i + 1,
            ref: messageRef,
            type: messageType,
            startSegment: startIndex,
            endSegment: endIndex
          },
          parsed
        });
      }
    }
  }
  
  return messageSlices;
};

/**
 * Parse the EDI content into segments (legacy function for compatibility)
 * @param {string} content - Raw EDI content
 * @param {object} delimiters - The delimiters used in the document
 * @returns {array} Array of segment objects
 */
const parseSegments = (content, delimiters) => {
  // Replace release characters before splitting
  let processedContent = content;
  if (delimiters.releaseCharacter) {
    processedContent = processReleaseCharacters(content, delimiters.releaseCharacter);
  }
  
  // Split into segments by segment terminator
  const segmentStrings = processedContent.split(delimiters.segmentTerminator)
    .map(s => s.trim())
    .filter(s => s.length > 0);
  
  // Parse each segment
  return segmentStrings.map(segmentString => {
    // Skip UNA segment as it's already processed
    if (segmentString.startsWith('UNA')) {
      return {
        id: 'UNA',
        elements: []
      };
    }
    
    // Parse data elements
    const elements = segmentString.split(delimiters.dataElementSeparator);
    const segmentId = elements.shift(); // First element is segment ID
    
    // Parse component data elements
    const parsedElements = elements.map(element => {
      if (element.includes(delimiters.componentDataSeparator)) {
        // Process component data elements
        const components = element.split(delimiters.componentDataSeparator);
        
        // Special handling for segments with known component structures
        if (segmentId === 'DTM' && components.length >= 2) {
          // Handle DTM segment components properly
          // Format: DTM+137:20220415:102' -> [137, 20220415, 102]
          return components;
        } else if (['NAD', 'CTA', 'RFF', 'MOA', 'CUX'].includes(segmentId)) {
          // These segments also have well-defined composite structures
          return components;
        } else {
          // Default handling for other composites
          return components;
        }
      }
      return element;
    });
    
    return {
      id: segmentId,
      elements: parsedElements
    };
  });
};

/**
 * Process and escape release characters in the content
 * @param {string} content - Raw EDI content
 * @param {string} releaseChar - The release character
 * @returns {string} Processed content
 */
const processReleaseCharacters = (content, releaseChar) => {
  // This is a simplistic implementation
  // In a production system, this would need to be more robust
  return content.replace(new RegExp(`\\${releaseChar}.`, 'g'), match => {
    // Replace the release character + the character it's releasing
    return match.charAt(1);
  });
};

/**
 * Safely parse a numeric string that may use comma or dot as decimal separator
 * @param {string|number|null|undefined} value
 * @returns {number|null}
 */
const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  const normalized = value.replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Extract dates from DTM segments and format them for display
 * @param {array} segments - Parsed segments
 * @returns {object} Extracted dates with their qualifiers and formatted values
 */
const extractDates = (segments) => {
  // Find all DTM segments
  const dtmSegments = segments.filter(seg => seg.id === 'DTM');
  const extractedDates = {};
  
  dtmSegments.forEach(segment => {
    try {
      if (segment.elements && segment.elements.length > 0) {
        let dtmElements;
        
        // Check if the element is an array (component data element)
        if (Array.isArray(segment.elements[0])) {
          dtmElements = segment.elements[0];
        } else {
          // If it's not an array, we might have single components or multiple elements
          if (segment.elements.length === 1 && segment.elements[0].includes(':')) {
            // Handle case where DTM components are in a single element with colons
            dtmElements = segment.elements[0].split(':');
          } else {
            // Handle case with multiple separate elements
            dtmElements = [];
            for (let i = 0; i < segment.elements.length; i++) {
              if (segment.elements[i]) {
                dtmElements.push(segment.elements[i]);
              }
            }
          }
        }
        
        // Element structure should be [qualifier, value, format qualifier]
        if (dtmElements.length >= 2) {
          const qualifier = dtmElements[0];
          const value = dtmElements[1];
          const format = dtmElements.length > 2 ? dtmElements[2] : '102'; // Default format if not specified
          
          extractedDates[qualifier] = {
            value: value,
            format: format,
            formatted: formatDate(value, format),
            qualifier: qualifier,
            qualifierMeaning: getDateQualifierMeaning(qualifier),
            formatMeaning: getDateFormatMeaning(format)
          };
        }
      }
    } catch (error) {
      console.error('Error extracting date from DTM segment:', error);
    }
  });
  
  return extractedDates;
};

/**
 * Get the meaning of a date qualifier code
 * @param {string} qualifier - Date qualifier code
 * @returns {string} The meaning of the qualifier or empty string
 */
const getDateQualifierMeaning = (qualifier) => {
  const meanings = {
    '2': 'Delivery date/time, requested',
    '3': 'Invoice date/time',
    '4': 'Order date/time',
    '7': 'Effective date/time',
    '10': 'Shipment date/time, requested',
    '11': 'Despatch date and/or time',
    '12': 'Terms delivery date/time',
    '13': 'Terms discount due date/time',
    '17': 'Delivery date/time, estimated',
    '35': 'Delivery date/time, actual',
    '36': 'Expiry date',
    '50': 'Goods receipt date/time',
    '63': 'Latest delivery date/time',
    '64': 'Earliest delivery date/time',
    '69': 'Delivery date/time, promised for',
    '71': 'Delivery date/time, requested for (after and including)',
    '72': 'Delivery date/time, requested for (before and including)',
    '76': 'Production/manufacture date',
    '137': 'Document/message date/time',
    '140': 'Payment due date',
    '171': 'Reference date/time',
    '200': 'Pick-up/collection date/time',
    '206': 'End date/time',
    '263': 'Invoicing period',
    '325': 'Tax period',
    '373': 'Order status date'
  };
  
  return meanings[qualifier] || '';
};

/**
 * Get the meaning of a date format code
 * @param {string} format - Date format code
 * @returns {string} The meaning of the format or empty string
 */
const getDateFormatMeaning = (format) => {
  const meanings = {
    '101': 'YYMMDD - Date without century',
    '102': 'CCYYMMDD - Date with full century',
    '104': 'MMWW - Month and week',
    '203': 'CCYYMMDDHHMM - Date/time with century and minutes',
    '204': 'CCYYMMDDHHMMSS - Date/time with century, minutes and seconds'
    // Add more as needed
  };
  
  return meanings[format] || '';
};

/**
 * Format a date string based on EDIFACT format qualifier
 * @param {string} value - The date value to format
 * @param {string} format - The EDIFACT format qualifier
 * @returns {string} Formatted date string
 */
const formatDate = (value, format) => {
  if (!value) return '';

  try {
    // Handle different EDIFACT date formats
    switch (format) {
      case '101': // YYMMDD
        return `20${value.substring(0, 2)}-${value.substring(2, 4)}-${value.substring(4, 6)}`;
      
      case '102': // CCYYMMDD
        return `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}`;
      
      case '104': // MMWW (Month-Week)
        return `Month ${value.substring(0, 2)}, Week ${value.substring(2, 4)}`;
      
      case '203': // CCYYMMDDHHMM
        return `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)} ${value.substring(8, 10)}:${value.substring(10, 12)}`;
      
      case '204': // CCYYMMDDHHMMSS
        return `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)} ${value.substring(8, 10)}:${value.substring(10, 12)}:${value.substring(12, 14)}`;
      
      default:
        return value; // Return as is if format is unknown
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return value;
  }
};

/**
 * Extract interchange header information
 * @param {array} segments - Parsed segments
 * @returns {object} Interchange header information
 */
const extractInterchangeInfo = (segments) => {
  // Find UNB segment (interchange header)
  const unbSegment = segments.find(seg => seg.id === 'UNB');
  
  if (!unbSegment) {
    return {
      sender: 'Unknown',
      recipient: 'Unknown',
      date: 'Unknown',
      controlRef: 'Unknown'
    };
  }
  
  try {
    const sender = Array.isArray(unbSegment.elements[1]) 
      ? unbSegment.elements[1][0] 
      : unbSegment.elements[1];
      
    const recipient = Array.isArray(unbSegment.elements[2]) 
      ? unbSegment.elements[2][0] 
      : unbSegment.elements[2];
    
    // Extract date and time from element 3
    let date = 'Unknown';
    if (unbSegment.elements[3]) {
      const datePart = Array.isArray(unbSegment.elements[3]) 
        ? unbSegment.elements[3][0] 
        : unbSegment.elements[3];
        
      const timePart = Array.isArray(unbSegment.elements[3]) && unbSegment.elements[3].length > 1 
        ? unbSegment.elements[3][1] 
        : '';
      
      date = datePart;
      if (timePart) date += ' ' + timePart;
    }
    
    const controlRef = unbSegment.elements[4] || 'Unknown';
    
    return {
      sender,
      recipient,
      date,
      controlRef
    };
  } catch (error) {
    console.error('Error extracting interchange info:', error);
    return {
      sender: 'Error',
      recipient: 'Error',
      date: 'Error',
      controlRef: 'Error'
    };
  }
};

/**
 * Extract message information
 * @param {array} segments - Parsed segments
 * @returns {object} Message information
 */
const extractMessageInfo = (segments) => {
  // Find UNH segment (message header)
  const unhSegment = segments.find(seg => seg.id === 'UNH');
  
  // Get interchange info for fallback sender/recipient ID
  const interchangeInfo = extractInterchangeInfo(segments);
  
  if (!unhSegment) {
    return {
      reference: 'Unknown',
      type: 'Unknown',
      version: 'Unknown',
      sender: { id: interchangeInfo.sender || 'Unknown', name: 'Unknown' },
      recipient: { id: interchangeInfo.recipient || 'Unknown', name: 'Unknown' },
      parties: {}
    };
  }
  
  try {
    const reference = unhSegment.elements[0] || 'Unknown';
    
    let type = 'Unknown';
    let version = 'Unknown';
    
    if (Array.isArray(unhSegment.elements[1])) {
      type = unhSegment.elements[1][0] || 'Unknown';
      
      // Version information
      if (unhSegment.elements[1].length > 2) {
        const ver = (unhSegment.elements[1][1] || '').toString();
        const rel = (unhSegment.elements[1][2] || '').toString();
        // Normalize common UNH version patterns like D + 96A => D96A for easier comparisons
        if (/^[A-Za-z]$/.test(ver) && /^[0-9]{2}[A-Za-z]$/.test(rel)) {
          version = `${ver}${rel}`.toUpperCase();
        } else {
          version = `${ver} ${rel}`.trim();
        }
      }
    }
    
    // Try to find sender and recipient from NAD segments
    let sender = extractPartyInfo(segments, 'SU');
    let recipient = extractPartyInfo(segments, 'BY');
    
    // Fallback to UNB sender/recipient if NAD didn't provide IDs
    if (sender.id === 'Unknown' && interchangeInfo.sender !== 'Unknown') {
      sender.id = interchangeInfo.sender;
    }
    
    if (recipient.id === 'Unknown' && interchangeInfo.recipient !== 'Unknown') {
      recipient.id = interchangeInfo.recipient;
    }
    
    // Extract all party codes from NAD segments for additional parties
    const parties = {};
    const nadSegments = segments.filter(seg => seg.id === 'NAD');
    
    // Party type codes we're interested in
    const partyCodes = ['BY', 'SU', 'SE', 'IV', 'DP', 'ST', 'CN', 'II', 'PE', 'UC'];
    
    partyCodes.forEach(code => {
      // Skip codes we've already included as sender or recipient
      if ((code === 'SU' && sender.type === 'SU') || (code === 'BY' && recipient.type === 'BY')) {
        return;
      }
      
      // Extract the party info
      const party = extractPartyInfo(segments, code);
      
      // Only add if we found this party type
      if (party.type !== 'Unknown') {
        parties[code] = party;
      }
    });
    
    return {
      reference,
      type,
      version,
      sender,
      recipient,
      parties
    };
  } catch (error) {
    console.error('Error extracting message info:', error);
    return {
      reference: 'Error',
      type: 'Error',
      version: 'Error',
      sender: { id: interchangeInfo.sender || 'Error', name: 'Error' },
      recipient: { id: interchangeInfo.recipient || 'Error', name: 'Error' },
      parties: {}
    };
  }
};

/**
 * Extract party information from NAD segments
 * @param {array} segments - Parsed segments
 * @param {string} partyCode - Party function code (e.g., 'BY' for buyer, 'SU' for supplier)
 * @returns {object} Party information
 */
const extractPartyInfo = (segments, partyCode) => {
  // First try to find an exact match for the requested party code
  const exactMatchSegment = segments.find(seg => 
    seg.id === 'NAD' && seg.elements[0] === partyCode
  );
  
  // If found, process this segment
  if (exactMatchSegment) {
    return extractPartyInfoFromSegment(exactMatchSegment);
  }
  
  // If exact match not found, try alternative codes as fallbacks
  let alternativeCodes = [];
  
  if (partyCode === 'BY') {
    alternativeCodes = ['IV', 'CN', 'ST']; // Buyer alternatives: Invoicee, Consignee, Ship to
  } else if (partyCode === 'SU') {
    alternativeCodes = ['SE', 'II']; // Supplier alternatives: Seller, Issuer of invoice
  }
  
  // Find NAD segment for any of the specified alternative party codes
  const alternativeSegment = segments.find(seg => 
    seg.id === 'NAD' && 
    alternativeCodes.includes(seg.elements[0])
  );
  
  if (alternativeSegment) {
    return extractPartyInfoFromSegment(alternativeSegment);
  }
  
  return { id: 'Unknown', name: 'Unknown', type: 'Unknown', typeDescription: 'Unknown' };
};

/**
 * Extract party information from a specific NAD segment
 * @param {object} nadSegment - NAD segment object
 * @returns {object} Party information
 */
const extractPartyInfoFromSegment = (nadSegment) => {
  try {
    let id = 'Unknown';
    let name = 'Unknown';
    let partyType = nadSegment.elements[0] || 'Unknown';
    
    // NAD format is typically: NAD+BY+12345::9'
    // Where element[1] might be an array containing the ID
    
    // Extract party ID from element 1 or 2 (depending on structure)
    if (nadSegment.elements.length > 1) {
      if (Array.isArray(nadSegment.elements[1])) {
        // If it's composite: NAD+BY+12345:23:9'
        id = nadSegment.elements[1][0] || 'Unknown';
      } else {
        // Direct value: NAD+BY+12345'
        id = nadSegment.elements[1] || 'Unknown';
      }
    }
    
    // If still unknown, try element 2 (some implementations vary)
    if (id === 'Unknown' && nadSegment.elements.length > 2) {
      if (Array.isArray(nadSegment.elements[2])) {
        id = nadSegment.elements[2][0] || 'Unknown';
      } else {
        id = nadSegment.elements[2] || 'Unknown';
      }
    }
    
    // Extract party name from element 3 or 4
    if (nadSegment.elements.length > 3) {
      name = nadSegment.elements[3] || 'Unknown';
    }
    
    // Map party type codes to descriptions
    const partyTypeDescriptions = {
      'BY': 'Buyer',
      'SU': 'Supplier',
      'SE': 'Seller',
      'IV': 'Invoicee',
      'DP': 'Delivery party',
      'ST': 'Ship to',
      'CN': 'Consignee',
      'II': 'Issuer of invoice'
    };
    
    return { 
      id, 
      name,
      type: partyType, 
      typeDescription: partyTypeDescriptions[partyType] || 'Unknown' 
    };
  } catch (error) {
    console.error(`Error extracting party info:`, error);
    return { id: 'Error', name: 'Error', type: 'Error', typeDescription: 'Error' };
  }
};

/**
 * Count occurrences of each segment type
 * @param {array} segments - Parsed segments
 * @returns {object} Count of each segment type
 */
const countSegmentTypes = (segments) => {
  const counts = {};
  
  segments.forEach(segment => {
    const id = segment.id;
    counts[id] = (counts[id] || 0) + 1;
  });
  
  return counts;
};

/**
 * Parse structured segments with additional context
 * @param {array} segments - Raw parsed segments
 * @returns {array} Structured segments with context
 */
const parseStructure = (segments) => {
  let currentSection = 'header';
  const sectionMap = {
    'header': [],
    'detail': [],
    'summary': []
  };
  
  // Process segments into sections
  segments.forEach(segment => {
    // Check for UNS segment which separates sections
    if (segment.id === 'UNS') {
      if (segment.elements && segment.elements.length > 0) {
        // UNS+S marks the boundary between detail and summary sections
        if (segment.elements[0] === 'S') {
          currentSection = 'summary';
        } else if (segment.elements[0] === 'D') {
          currentSection = 'detail';
        }
      }
      
      // Add the UNS segment to the current section
      sectionMap[currentSection].push({
        ...segment,
        section: currentSection,
        isSectionDivider: true
      });
      
      return;
    }
    
    // Add segment to current section
    sectionMap[currentSection].push({
      ...segment,
      section: currentSection
    });
  });
  
  // Combine all sections in order
  return [...sectionMap.header, ...sectionMap.detail, ...sectionMap.summary];
};

/**
 * Calculate total monetary amounts from MOA segments in the summary section
 * @param {array} summarySegments - Segments from the summary section
 * @returns {object} Monetary totals
 */
const calculateTotalAmount = (summarySegments) => {
  const totals = {
    documentTotal: null,
    taxTotal: null,
    lineItemTotal: null
  };
  
  // Look for MOA segments
  const moaSegments = summarySegments.filter(seg => seg.id === 'MOA');
  
  moaSegments.forEach(segment => {
    try {
      if (segment.elements && segment.elements.length > 0 && Array.isArray(segment.elements[0])) {
        const qualifier = segment.elements[0][0];
        const amount = segment.elements[0][1];
        
        // Process based on qualifier
        // Common qualifiers:
        // 86 = Message total monetary amount
        // 79 = Total line items amount
        // 176 = Message total duty/tax/fee amount
        // 124 = Tax amount
        if (qualifier === '86') {
          totals.documentTotal = parseFloat(amount);
        } else if (qualifier === '79') {
          totals.lineItemTotal = parseFloat(amount);
        } else if (qualifier === '176' || qualifier === '124') {
          totals.taxTotal = parseFloat(amount);
        }
      }
    } catch (error) {
      console.error('Error extracting monetary amount:', error);
    }
  });
  
  return totals;
};

/**
 * Extract document totals and line counts from the message
 * @param {array} segments - All parsed segments
 * @returns {object} Document totals and counts
 */
const extractDocumentTotals = (segments) => {
  const totals = {
    // Line counts
    lineCount: 0,
    packageCount: 0,
    segmentCount: 0,
    
    // Monetary amounts
    documentTotal: null,
    taxTotal: null,
    lineItemTotal: null,
    taxableAmount: null,
    vatAmount: null,
    grossTotal: null,
    
    // Currency information
    currency: null,
    // VAT breakdown by rate/category from summary TAX/MOA
    taxBreakdown: []
  };
  
  // Count LIN segments to get the number of line items
  totals.lineCount = segments.filter(seg => seg.id === 'LIN').length;
  
  // Extract totals from CNT segments
  const cntSegments = segments.filter(seg => seg.id === 'CNT');
  cntSegments.forEach(segment => {
    try {
      if (segment.elements && segment.elements.length > 0 && Array.isArray(segment.elements[0])) {
        const qualifier = segment.elements[0][0];
        const value = segment.elements[0][1];
        
        // Process based on qualifier
        // 1 = Total number of line items
        // 2 = Total number of messages
        // 11 = Total number of packages
        // 2E = Number of segments
        if (qualifier === '1') {
          totals.lineCount = parseInt(value, 10) || 0;
        } else if (qualifier === '11') {
          totals.packageCount = parseInt(value, 10) || 0;
        } else if (qualifier === '2E') {
          totals.segmentCount = parseInt(value, 10) || 0;
        }
      }
    } catch (error) {
      console.error('Error extracting CNT value:', error);
    }
  });

  // Extract currency from CUX segment
  const cuxSegments = segments.filter(seg => seg.id === 'CUX');
  if (cuxSegments.length > 0) {
    try {
      const cuxSegment = cuxSegments[0];
      if (cuxSegment.elements && cuxSegment.elements.length > 0 && Array.isArray(cuxSegment.elements[0])) {
        // Usually format is CUX+2:EUR:4
        const currency = cuxSegment.elements[0][1];
        if (currency) {
          totals.currency = currency;
        }
      }
    } catch (error) {
      console.error('Error extracting currency:', error);
    }
  }

  // Extract monetary totals
  // Find summary section
  let summarySegments = [];
  let inSummary = false;
  
  for (const segment of segments) {
    if (segment.id === 'UNS' && segment.elements && segment.elements[0] === 'S') {
      inSummary = true;
      continue;
    }
    
    if (inSummary) {
      summarySegments.push(segment);
    }
  }

  // Calculate monetary totals from MOA segments in summary section
  const moaSegments = summarySegments.filter(seg => seg.id === 'MOA');
  moaSegments.forEach(segment => {
    try {
      if (segment.elements && segment.elements.length > 0 && Array.isArray(segment.elements[0])) {
        const qualifier = segment.elements[0][0];
        const amount = segment.elements[0][1];
        
        // Process based on qualifier
        if (qualifier === '86') {
          totals.documentTotal = parseNumber(amount);
        } else if (qualifier === '79') {
          totals.lineItemTotal = parseNumber(amount);
        } else if (qualifier === '176' || qualifier === '124') {
          totals.taxTotal = parseNumber(amount);
          // MOA 124 often represents tax amount – keep alias
          if (qualifier === '124') totals.vatAmount = parseNumber(amount);
        } else if (qualifier === '125') {
          // Taxable amount (basis)
          totals.taxableAmount = parseNumber(amount);
        } else if (qualifier === '150') {
          // VAT amount (alternate qualifier)
          totals.vatAmount = parseNumber(amount);
        } else if (qualifier === '388') {
          // Total amount including VAT (per some profiles)
          totals.grossTotal = parseNumber(amount);
        }
      }
    } catch (error) {
      console.error('Error extracting monetary amount:', error);
    }
  });

  // Build VAT breakdown by scanning summary TAX → MOA groups
  // Pattern: TAX (with rate/category) followed by MOA 125/124/150 amounts
  let i = 0;
  while (i < summarySegments.length) {
    const seg = summarySegments[i];
    if (seg.id === 'TAX') {
      let rate = null;
      let category = null;
      let type = null;
      try {
        // TAX+7+VAT+++:::25+S'
        type = seg.elements?.[1] || null;
        // Rate commonly in element 5 as ':::25' or composite with index 3
        const e4 = seg.elements?.[4];
        if (typeof e4 === 'string') {
          if (e4.startsWith(':::')) rate = parseNumber(e4.slice(3));
          else rate = parseNumber(e4);
        } else if (Array.isArray(e4) && e4.length >= 4) {
          rate = parseNumber(e4[3]);
        } else {
          // Fallback: scan stringified elements
          const s = JSON.stringify(seg.elements || []);
          const m = s.match(/:::(\d{1,2}(?:[\.,]\d+)?)/);
          if (m) rate = parseNumber(m[1]);
        }
        // Category often at element 6 (e.g., 'S', 'Z')
        const e5 = seg.elements?.[5];
        if (typeof e5 === 'string') category = e5;
        else if (Array.isArray(e5) && e5[0]) category = e5[0];
      } catch {}

      // Collect following MOA values until next TAX or end of summary
      let taxableAmount = null;
      let taxAmount = null;
      let j = i + 1;
      while (j < summarySegments.length && summarySegments[j].id !== 'TAX') {
        const mseg = summarySegments[j];
        if (mseg.id === 'MOA' && Array.isArray(mseg.elements?.[0])) {
          const [q, amt] = mseg.elements[0];
          if (q === '125') taxableAmount = parseNumber(amt);
          if (q === '124' || q === '150') taxAmount = parseNumber(amt);
        }
        j++;
      }
      totals.taxBreakdown.push({ type, rate, category, taxableAmount, taxAmount });
      i = j; // continue after this TAX block
      continue;
    }
    i++;
  }
  
  return totals;
};

/**
 * Extract line items with PIA and IMD information
 * @param {array} segments - All parsed segments
 * @returns {array} Line items with product identifications and descriptions
 */
const extractLineItems = (segments) => {
  const lineItems = [];
  let currentLineItem = null;
  let inDetailSection = false;
  let currentLineNumber = 0;
  
  // Process segments to extract line items
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    
    // Check if we're entering the detail section
    if (segment.id === 'UNS') {
      if (segment.elements && segment.elements.length > 0 && segment.elements[0] === 'D') {
        inDetailSection = true;
      } else if (segment.elements && segment.elements.length > 0 && segment.elements[0] === 'S') {
        inDetailSection = false;
      }
    }
    
    // Start of a new line item with LIN segment
    if (segment.id === 'LIN') {
      // If there's a current line item being processed, add it to the array
      if (currentLineItem) {
        lineItems.push(currentLineItem);
      }
      
      // Get the line number from the first element
      currentLineNumber = segment.elements[0] || (currentLineNumber + 1);
      
      // Initialize a new line item
      currentLineItem = {
        lineNumber: currentLineNumber,
        productId: '',
        idType: '',
        additionalIds: [],
        description: '',
        quantity: null,
        price: null,
        itemTotal: null,
        dates: {}
      };
      
      // Extract primary product ID from LIN if available
      // LIN+1+3+T  623807:IN'
      if (segment.elements.length > 2) {
        // Third element could be the actual product ID or a composite element
        if (segment.elements[2] && typeof segment.elements[2] === 'string') {
          // Direct value
          currentLineItem.productId = segment.elements[2];
        } else if (Array.isArray(segment.elements[2]) && segment.elements[2].length > 0) {
          // Composite value (product ID and scheme)
          currentLineItem.productId = segment.elements[2][0] || '';
          currentLineItem.idType = segment.elements[2].length > 1 ? segment.elements[2][1] : '';
        }
      }
    }
    
    // If we're not currently processing a line item, skip
    if (!currentLineItem) continue;
    
    // Process PIA segments (Additional Product IDs)
    // PIA+5+423807:SA' or PIA+1+T  623807:BP'
    if (segment.id === 'PIA') {
      try {
        // First element is the qualifier (e.g., 1, 5)
        const qualifier = segment.elements[0];
        
        // Loop through remaining elements which could be product identifications
        for (let j = 1; j < segment.elements.length; j++) {
          let productId = '';
          let idScheme = '';
          
          // Element could be a string or an array
          if (typeof segment.elements[j] === 'string') {
            // Direct value
            productId = segment.elements[j];
          } else if (Array.isArray(segment.elements[j])) {
            // Composite value with ID and scheme
            productId = segment.elements[j][0] || '';
            idScheme = segment.elements[j].length > 1 ? segment.elements[j][1] : '';
          }
          
          // Only add if we have a product ID
          if (productId) {
            currentLineItem.additionalIds.push({
              qualifier,
              productId,
              idScheme,
              qualifierMeaning: getPIAQualifierMeaning(qualifier)
            });
          }
        }
      } catch (error) {
        console.error('Error extracting PIA information:', error);
      }
    }
    
    // Process IMD segments (Item Description)
    // IMD+C++:::Motorvärmare 230V'
    if (segment.id === 'IMD') {
      try {
        // In IMD segments, the description can be in various places depending on format
        
        // Check each element where description could be
        for (let j = 1; j < segment.elements.length; j++) {
          let description = '';
          
          if (typeof segment.elements[j] === 'string' && segment.elements[j]) {
            // Direct description value
            description = segment.elements[j];
            currentLineItem.description = description;
            break;
          } else if (Array.isArray(segment.elements[j])) {
            // Check through the composite elements
            for (let k = 0; k < segment.elements[j].length; k++) {
              if (segment.elements[j][k]) {
                description = segment.elements[j][k];
                currentLineItem.description = description;
                break;
              }
            }
            if (description) break;
          }
        }
      } catch (error) {
        console.error('Error extracting IMD information:', error);
      }
    }
    
    // Process QTY segments (Quantity)
    // QTY+113:1:PCE'
    if (segment.id === 'QTY') {
      try {
        if (segment.elements && segment.elements.length > 0) {
          let qualifier = '';
          let quantity = null;
          let unit = '';
          
          if (Array.isArray(segment.elements[0])) {
            // Format is QTY+113:1:PCE' (qualifier:quantity:unit)
            qualifier = segment.elements[0][0] || '';
            quantity = segment.elements[0].length > 1 ? segment.elements[0][1] : null;
            unit = segment.elements[0].length > 2 ? segment.elements[0][2] : '';
          } else {
            // Format might be QTY+113+1+PCE'
            qualifier = segment.elements[0];
            quantity = segment.elements.length > 1 ? segment.elements[1] : null;
            unit = segment.elements.length > 2 ? segment.elements[2] : '';
          }
          
          if (qualifier && quantity !== null) {
            currentLineItem.quantity = {
              value: parseFloat(quantity),
              qualifier: qualifier,
              unit: unit,
              qualifierMeaning: getQTYQualifierMeaning(qualifier)
            };
          }
        }
      } catch (error) {
        console.error('Error extracting QTY information:', error);
      }
    }
    
    // Process DTM segments related to the line item
    // DTM+76:20250414:102' or DTM+35:20250411:102'
    if (segment.id === 'DTM') {
      try {
        if (segment.elements && segment.elements.length > 0) {
          let qualifier = '';
          let dateValue = '';
          let formatQualifier = '';
          
          if (Array.isArray(segment.elements[0])) {
            // Format is DTM+76:20250414:102'
            qualifier = segment.elements[0][0] || '';
            dateValue = segment.elements[0].length > 1 ? segment.elements[0][1] : '';
            formatQualifier = segment.elements[0].length > 2 ? segment.elements[0][2] : '';
          } else {
            // Alternative format
            qualifier = segment.elements[0];
            dateValue = segment.elements.length > 1 ? segment.elements[1] : '';
            formatQualifier = segment.elements.length > 2 ? segment.elements[2] : '';
          }
          
          if (qualifier && dateValue) {
            currentLineItem.dates[qualifier] = {
              value: dateValue,
              format: formatQualifier,
              formatted: formatDate(dateValue, formatQualifier),
              qualifierMeaning: getDateQualifierMeaning(qualifier)
            };
          }
        }
      } catch (error) {
        console.error('Error extracting DTM information for line item:', error);
      }
    }
    
    // Process PRI segments (Price)
    if (segment.id === 'PRI') {
      try {
        if (segment.elements && segment.elements.length > 0) {
          let qualifier = '';
          let price = null;
          
          if (Array.isArray(segment.elements[0])) {
            // Format is PRI+AAA:100'
            qualifier = segment.elements[0][0] || '';
            price = segment.elements[0].length > 1 ? segment.elements[0][1] : null;
          } else {
            // Alternative format
            qualifier = segment.elements[0];
            price = segment.elements.length > 1 ? segment.elements[1] : null;
          }
          
          if (qualifier && price !== null) {
            currentLineItem.price = {
              value: parseFloat(price),
              qualifier: qualifier,
              qualifierMeaning: getPRIQualifierMeaning(qualifier)
            };
          }
        }
      } catch (error) {
        console.error('Error extracting PRI information:', error);
      }
    }
    
    // Process MOA segments (Monetary Amount) for line item
    if (segment.id === 'MOA') {
      try {
        if (segment.elements && segment.elements.length > 0) {
          let qualifier = '';
          let amount = null;
          
          if (Array.isArray(segment.elements[0])) {
            // Format is MOA+203:150.00'
            qualifier = segment.elements[0][0] || '';
            amount = segment.elements[0].length > 1 ? segment.elements[0][1] : null;
          } else {
            // Alternative format
            qualifier = segment.elements[0];
            amount = segment.elements.length > 1 ? segment.elements[1] : null;
          }
          
          // If line item amount (common qualifiers for line amounts)
          if (qualifier && amount !== null && ['203', '128', '38', '66', '79'].includes(qualifier)) {
            currentLineItem.itemTotal = {
              value: parseFloat(amount),
              qualifier: qualifier
            };
          }
        }
      } catch (error) {
        console.error('Error extracting MOA information for line item:', error);
      }
    }

    // Process TAX segments (VAT at line level)
    if (segment.id === 'TAX') {
      try {
        let taxType = null;
        let category = null;
        let rate = null;
        if (segment.elements && segment.elements.length > 0) {
          taxType = segment.elements[1] || null; // Expect 'VAT'
          // Rate often at elements[4]
          const e4 = segment.elements[4];
          if (typeof e4 === 'string') {
            if (e4.startsWith(':::')) rate = parseNumber(e4.slice(3));
            else rate = parseNumber(e4);
          } else if (Array.isArray(e4) && e4.length >= 4) {
            rate = parseNumber(e4[3]);
          }
          // Category often element[5]
          const e5 = segment.elements[5];
          if (typeof e5 === 'string') category = e5;
          else if (Array.isArray(e5) && e5[0]) category = e5[0];
        }
        currentLineItem.tax = { type: taxType, category, rate };
      } catch (error) {
        console.error('Error extracting TAX for line item:', error);
      }
    }
    
    // If we detect a new LIN or UNS segment coming up, we've finished the current line item
    if (i < segments.length - 1 && 
        (segments[i + 1].id === 'LIN' || segments[i + 1].id === 'UNS')) {
      if (currentLineItem) {
        lineItems.push(currentLineItem);
        currentLineItem = null;
      }
    }
  }
  
  // Add the last line item if exists
  if (currentLineItem) {
    lineItems.push(currentLineItem);
  }
  
  // Log the first line item for debugging
  if (lineItems.length > 0) {
    console.log('First line item parsed:', lineItems[0]);
  }
  
  return lineItems;
};

/**
 * Get the meaning of a PIA qualifier code
 * @param {string} qualifier - PIA qualifier code
 * @returns {string} The meaning of the qualifier or the qualifier itself
 */
const getPIAQualifierMeaning = (qualifier) => {
  const meanings = {
    '1': 'Additional identification',
    '2': 'Product identification',
    '3': 'Supplier\'s identification',
    '4': 'Shipment identification',
    '5': 'Product group'
  };
  
  return meanings[qualifier] || qualifier;
};

/**
 * Get the meaning of a QTY qualifier code
 * @param {string} qualifier - QTY qualifier code
 * @returns {string} The meaning of the qualifier or the qualifier itself
 */
const getQTYQualifierMeaning = (qualifier) => {
  const meanings = {
    '1': 'Discrete quantity',
    '11': 'Split quantity',
    '12': 'Despatch quantity',
    '17': 'Quantity on hand',
    '18': 'Previous quantity',
    '21': 'Ordered quantity',
    '22': 'Promotional quantity',
    '33': 'Density factor',
    '35': 'Delivered quantity',
    '40': 'Normal delivery quantity',
    '41': 'Maximum supply quantity',
    '42': 'Minimum order quantity',
    '46': 'Delivered quantity',
    '47': 'Invoiced quantity',
    '48': 'Received quantity',
    '52': 'Quantity per pack',
    '59': 'Number of consumer units in a trading unit',
    '61': 'Return quantity',
    '66': 'Cumulative quantity',
    '83': 'Backorder quantity',
    '113': 'Quantity shipped, to date',
    '116': 'Accepted quantity difference',
    '119': 'Prior excess quantity',
    '128': 'Item gross weight',
    '129': 'Item net weight',
    '192': 'Free goods quantity'
  };
  
  return meanings[qualifier] || qualifier;
};

/**
 * Get the meaning of a PRI qualifier code
 * @param {string} qualifier - PRI qualifier code
 * @returns {string} The meaning of the qualifier or the qualifier itself
 */
const getPRIQualifierMeaning = (qualifier) => {
  const meanings = {
    'AAA': 'Net calculation',
    'AAB': 'Gross calculation',
    'AAE': 'Information price, excluding allowances/charges',
    'INF': 'Information',
    'INV': 'Invoice price',
    'NTP': 'Net unit price',
    'PRP': 'Promotional price'
  };
  
  return meanings[qualifier] || qualifier;
};

export default eancomParser; 
export { buildParsedFromSegments };